import * as React from 'react';
import { type FunctionComponent, useEffect, useState } from 'react';

import { Spinner } from '@blueprintjs/core';

import { ContractStateFilter } from '../../../generated-sources/openapi';
import i18n from '../../../i18n/i18n';
import { IconDownload } from '../../../icons/svg/IconDownload';
import { Button } from '../../../lib/Button';
import { PageableDatasource } from '../../../lib/Table';
import { downloadContractStates } from '../../../services/ApiService';
import { formatDuration, testAttribute } from '../../../util/Util';
import { alertToast } from '../../Toast/AlertToast';

const BulkContractStateDownloadButton: FunctionComponent<{
  filter: ContractStateFilter;
  contractStateDatasource?: PageableDatasource<any>;
}> = ({ filter, contractStateDatasource }) => {
  const [loading, setLoading] = useState(false);
  const [timeEstimate, setTimeEstimate] = useState<string | null>('');
  const [abortController, setAbortController] = useState<AbortController | undefined>();

  useEffect(
    () => () => {
      // Stop download if the component is unmounted
      abortController?.abort();
    },
    [abortController],
  );

  function concatUint8Arrays(chunks: any) {
    const totalLength = chunks.reduce((sum: any, value: any) => sum + value.length, 0);
    const result = new Uint8Array(totalLength);

    let length = 0;
    for (const array of chunks) {
      result.set(array, length);
      length += array.length;
    }

    return result;
  }

  function hasAnyFilterValue() {
    return (
      Object.values(filter)
        .filter(x => x !== null)
        .filter(x => x !== undefined).length > 0
    );
  }

  const download = () => {
    setLoading(true);
    const newAbortController = new AbortController();
    setAbortController(newAbortController);
    downloadContractStates(filter, newAbortController)
      .then(async response => {
        if (!response.body) return;
        const reader = response.body.getReader();
        let receivedLength = 0;
        const chunks = [];
        let totalRows = 0;
        const startTime = Date.now();

        // Download the actual csv here
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          chunks.push(value);
          receivedLength += value.length;
          const rows = new TextDecoder('utf-8').decode(value).split('\n').length;
          totalRows += rows;

          // Calculate remaining time estimate
          const elapsedTime = (Date.now() - startTime) / 1000; // Time in seconds
          const averageTimePerRow = elapsedTime / totalRows;
          const estimatedTotalTime = averageTimePerRow * (contractStateDatasource?.totalItemCount ?? 1);
          const estimatedRemainingTime = estimatedTotalTime - elapsedTime;
          if (estimatedRemainingTime > 10) {
            setTimeEstimate(formatDuration(estimatedRemainingTime));
          } else {
            setTimeEstimate(null);
          }
        }

        const csvContent = new TextDecoder('utf-8').decode(concatUint8Arrays(chunks));

        // Create a blob from the CSV content
        const blob = new Blob([csvContent], { type: 'text/csv' });

        // Create a link and trigger a download
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(blob);
        downloadLink.download = 'contract_states.csv';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        alertToast(err);
      });
  };

  if (loading)
    return (
      <Button type="tertiary" testId={testAttribute('dnlw', 'action-page.downloading')} icon={<Spinner size={10} />}>
        {i18n.t('action-page.downloading')} {timeEstimate && <>({timeEstimate})</>}
      </Button>
    );

  return (
    <Button
      disabled={!hasAnyFilterValue()}
      onClick={download}
      type="tertiary"
      icon={<IconDownload />}
      testId={testAttribute('dnlw', 'action-page.downloading')}
      tooltip={
        hasAnyFilterValue() ? i18n.t('action-page.download') : i18n.t('contract-state.download-disabled-explanation')
      }
    />
  );
};

export { BulkContractStateDownloadButton };
